/**
 * TRACI-1012: listens for the `traci:secure-form` window event (raised by the live-chat poll
 * when the rep sends a LivePerson secure form) and shows the form in its OWN React root,
 * mounted on document.body. Keeping it out of the chat's React tree keeps it out of the
 * chat's message history, interaction tracking and analytics entirely.
 */
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { SecureFormModal, SecureFormRequest } from './SecureFormModal';
import { formKind } from './secureFormOrigins';

export const SECURE_FORM_EVENT = 'traci:secure-form';
/** Fired with {state: 'sent' | 'submitted', agentName} so the chat can show a status line. */
export const SECURE_FORM_STATUS_EVENT = 'traci:secure-form-status';

const SEEN_KEY = 'traci_secure_form_seen';

let installed = false;
let root: Root | null = null;
let host: HTMLDivElement | null = null;

function seen(): string[] {
  try { return JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]'); } catch { return []; }
}

function markSeen(id: string) {
  try { sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen(), id].slice(-20))); } catch { /* storage blocked */ }
}

function close() {
  root?.unmount();
  root = null;
  host?.remove();
  host = null;
}

function open(req: SecureFormRequest) {
  close();
  host = document.createElement('div');
  host.setAttribute('data-secure-form-root', '');
  document.body.appendChild(host);
  root = createRoot(host);
  root.render(<SecureFormModal request={req} onClose={close} />);
}

export function installSecureForm(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener(SECURE_FORM_EVENT, (e: Event) => {
    const d = (e as CustomEvent).detail as Partial<SecureFormRequest> | undefined;
    if (!d || typeof d.url !== 'string' || typeof d.invitationId !== 'string') return;
    if ((d as { scrubbed?: unknown }).scrubbed) return;  // an expired replay: nothing to show
    if (!formKind(d.url)) return;  // LivePerson's form page, or an origin this environment's config lists
    if (seen().includes(d.invitationId)) return;
    markSeen(d.invitationId);
    window.dispatchEvent(new CustomEvent(SECURE_FORM_STATUS_EVENT, { detail: { state: 'sent', agentName: d.agentName } }));
    open(d as SecureFormRequest);
  });
}

/** Close an open secure form (the live chat ended or the customer left it). */
export function closeSecureForm(): void {
  close();
}
